/**
 * Supabase client for Bastion applications
 * 
 * This is designed to work in both browser (webapp) and Electron (collector) contexts.
 */

import { createClient as createSupabaseClient, SupabaseClient } from '@supabase/supabase-js';

let supabaseInstance: SupabaseClient | null = null;

/**
 * Auth configuration
 */
export interface AuthConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
}

/**
 * Default configuration (uses environment variables)
 * Supports Vite (VITE_), Next.js (NEXT_PUBLIC_), and plain env vars
 */
function getDefaultConfig(): AuthConfig {
  // For Vite, use import.meta.env; for Node, use process.env
  const viteEnv = typeof import.meta !== 'undefined' ? (import.meta as any).env : {};
  const processEnv = typeof process !== 'undefined' ? process.env : {};

  const supabaseUrl = 
    viteEnv?.VITE_SUPABASE_URL ||
    processEnv?.SUPABASE_URL ||
    processEnv?.NEXT_PUBLIC_SUPABASE_URL ||
    'https://placeholder.supabase.co';
    
  // Support both old "anon key" and new "publishable key" naming
  const supabaseAnonKey = 
    viteEnv?.VITE_SUPABASE_PUBLISHABLE_KEY ||
    viteEnv?.VITE_SUPABASE_ANON_KEY ||
    processEnv?.SUPABASE_PUBLISHABLE_KEY ||
    processEnv?.SUPABASE_ANON_KEY ||
    processEnv?.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
    'placeholder-key';

  return { supabaseUrl, supabaseAnonKey };
}

/**
 * Options for client creation beyond the base config.
 */
export interface ClientOptions {
  /**
   * Whether to detect OAuth session data in the URL (hash/query params).
   * - `true` for web apps (standard redirect-based OAuth flow)
   * - `false` for desktop/Tauri apps (loopback server captures the code)
   * Defaults to `false` for backward compatibility with Tauri.
   */
  detectSessionInUrl?: boolean;
}

/**
 * Create or get the Supabase client singleton.
 *
 * IMPORTANT: The first call determines the client configuration for its
 * entire lifetime (singleton). Subsequent calls return the same instance.
 */
export function createClient(
  config?: Partial<AuthConfig>,
  options?: ClientOptions,
): SupabaseClient {
  if (supabaseInstance) {
    return supabaseInstance;
  }

  const defaultConfig = getDefaultConfig();
  const finalConfig = { ...defaultConfig, ...config };

  if (finalConfig.supabaseUrl === 'https://placeholder.supabase.co') {
    console.warn(
      '[Auth] Supabase credentials not configured. ' +
      'Set SUPABASE_URL and SUPABASE_ANON_KEY environment variables.'
    );
  }

  supabaseInstance = createSupabaseClient(
    finalConfig.supabaseUrl,
    finalConfig.supabaseAnonKey,
    {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: options?.detectSessionInUrl ?? false,
        flowType: 'pkce',
      },
    }
  );

  return supabaseInstance;
}

/**
 * Get the existing Supabase client (throws if not initialized)
 */
export function getClient(): SupabaseClient {
  if (!supabaseInstance) {
    throw new Error('[Auth] Supabase client not initialized. Call createClient() first.');
  }
  return supabaseInstance;
}

/**
 * Reset the client (useful for testing or re-initialization)
 */
export function resetClient(): void {
  supabaseInstance = null;
}

/** Subscribe to auth credentials without exposing the database client.
 * Includes the initial session, token refresh, account changes, and sign-out.
 * Returns the subscription cleanup function.
 */
export function subscribeAuthCredentials(
  listener: (accessToken: string | null, userId: string | null) => void,
): () => void {
  const { data: { subscription } } = getClient().auth.onAuthStateChange(
    (_event, session) => listener(session?.access_token ?? null, session?.user.id ?? null),
  );
  return () => subscription.unsubscribe();
}
